import { EQueryParams } from './enums';

/**
 * Publishers measure the widget -> web step by looking for a login or a page
 * event per player, and count anything else as a drop-off. A player who opens
 * the portal from the game and leaves before finishing OTP produces neither, so
 * the visit is indistinguishable from never having happened.
 *
 * Unlike the store's pre-auth handoff, nothing the portal is handed contains a
 * player id — OTP credentials are opaque publisher artifacts. So the landing is
 * recorded with the app session id instead, which the backend and publisher
 * events do carry, making an anonymous session joinable to the player id the
 * publisher is asking about.
 */
export enum EPortalLandingCredential {
    OTP = 'otp',
    CAMPAIGN = 'campaign',
    APPLE = 'apple',
    NONE = 'none',
}

/** Not in EQueryParams because only landing classification ever reads it. */
export const PROOF_KEY_PARAM = 'proofKey';
export const JOURNEY_ID_PARAM = 'journey_id';
const JOURNEY_ID_PATTERN = /^[a-zA-Z0-9-]{1,128}$/;

/** Named once for both apps, so one query can follow a landing across either. */
export { LANDING_CREDENTIAL_HEADER, LANDING_JOURNEY_HEADER, LANDING_KEY_HEADER } from '@web-store-platforms/utils';

/**
 * When the middleware received the navigation, in epoch milliseconds, for the
 * render to measure itself against. Set only on a document navigation, so the
 * render logs once per landing and not once per payload refetch; stripped from
 * the incoming request like the others, so a caller cannot backdate a landing.
 */
export const LANDING_RECEIVED_AT_HEADER = 'x-ac-landing-received-at';

/**
 * The landing's own query string, for the render to log the player in with
 * before the document leaves the server. Set only on an OTP document
 * navigation; travels one hop, from the middleware to the render, and is never
 * logged. Stripped from the incoming request like the others.
 */
export const LANDING_SEARCH_HEADER = 'x-ac-landing-search';

interface PortalLandingInput {
    pathname: string;
    search: string;
    platform?: string;
    storedPlayerId?: string;
    appSessionId?: string;
    journeyId?: string;
}

export const describePortalLanding = ({
    pathname,
    search,
    platform,
    storedPlayerId,
    appSessionId,
    journeyId,
}: PortalLandingInput): Record<string, unknown> => ({
    pathname,
    platform,
    credential: resolveLandingCredential(new URLSearchParams(search)),
    playerId: storedPlayerId,
    isReturningPlayer: Boolean(storedPlayerId),
    appSessionId,
    journeyId,
});

/**
 * Credential types only — never the values, which are live login credentials.
 * Shared with the middleware so the server-side record of a landing and the
 * client-side one cannot disagree about what kind of landing it was.
 */
export const resolveLandingCredential = (params: URLSearchParams): EPortalLandingCredential => {
    if (params.get(EQueryParams.TOKEN) && params.get(EQueryParams.FROM) === 'campaign') {
        return EPortalLandingCredential.CAMPAIGN;
    }
    if (params.has(PROOF_KEY_PARAM) && params.has(EQueryParams.TOKEN)) {
        return EPortalLandingCredential.OTP;
    }
    if (params.has('apple')) {
        return EPortalLandingCredential.APPLE;
    }
    return EPortalLandingCredential.NONE;
};

export const readPortalJourneyId = (
    params: URLSearchParams
): string | undefined => {
    const journeyId = params.get(JOURNEY_ID_PARAM);
    if (!journeyId || !JOURNEY_ID_PATTERN.test(journeyId)) {
        return undefined;
    }
    return journeyId;
};
